import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { TranslateService } from '@ngx-translate/core';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as liftActions from './lifts.actions';
import {
    AddSlotAction,
    AddToolsToBatchAction,
    CopySlotAction,
    CopyBatchAction,
    CreateBatchWithToolsAction, DeleteBatchAction, DeleteSlotAction,
    DuplicateSlotAction,
    LoadAllToolsAction, LoadAllToolsBySlotAction,
    LoadBatchAction,
    LoadCopiedSlotAction,
    LoadDuplicatedSlotAction,
    LoadLiftAction,
    LoadSlotAction,
    LoadTrayDetailsAction,
    RemoveToolsFromBatchAction,
    RenameSlotAction,
    UpdateBatchAction,
    UpdateCartBatchToolsAction,
    DuplicateBatchAction, LoadLiftsAction
} from './lifts.actions';
import { LiftsService } from '@app/lift/service/lifts.service';
import { Lift } from '@models/lift.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Tray } from '@models/tray.model';
import { Slot } from '@models/slot.model';
import { ToolService } from '@services/tool.service';
import { Batch } from '@models/batch.model';
import { BatchService } from '@app/lift/service/batch.service';
import { Group } from '@app/tool/model/group.model';
import { GroupChildWrapper } from '@app/tool/model/group-child-wrapper.model';
import { PlantService } from '@services/plant.service';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { Plant } from '@models/plant.model';
import 'rxjs/add/operator/map'
import { of } from "rxjs";
import * as actions from "@app/strain/registry/store/strain-registry.actions";
import { LineService } from "@services/line.service";
import { Line } from "@models/line.model";

@Injectable()

export class LiftsEffects {

    // tslint:disable-next-line
    loadLifts$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_LIFTS),
        switchMap((loadAction: any) => {
                return this.liftService.getLifts(loadAction.config).pipe(
                    map((lifts: any[]) => {
                        return new liftActions.LoadLiftsSuccessAction(this.mapLifts(lifts), loadAction.config.pager.page === 1)
                    }),
                    catchError(error => {
                        console.error('Error in effect:', error);
                        return of(new liftActions.LoadLiftsFailedAction(error));
                    }))
            }
        )));

    // tslint:disable-next-line
    loadPlants$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_PLANTS),
        switchMap((loadAction: any) => {
            return this.plantService.getAllPlants(new TableConfig()).pipe(
                map((lifts: any) => {
                    return new liftActions.LoadPlantsSuccessAction(this.mapPlants(lifts))
                }),
                catchError((error) => of(new liftActions.LoadPlantsFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    loadLines$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_LINES),
        switchMap((action: any) => {
            const config = new TableConfig();
            config.pager.pageSize = -1;
            return this.lineService.getAllLines(config).pipe(
                map((payload: any) => {
                    return new liftActions.LoadLinesSuccessAction(this.mapLines(payload))
                }),
                catchError((error) => of(new liftActions.LoadLinesFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    getLift$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_LIFT),
        switchMap((loadLiftAction: LoadLiftAction) => {
            return this.liftService.getLift(loadLiftAction.id, loadLiftAction.filters).pipe(
                map((lifts: any) => {
                    return new liftActions.LoadLiftSuccessAction(this.mapLift(lifts))
                }),
                catchError((error) => of(new liftActions.LoadLiftFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    addLift$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.ADD_LIFT),
        switchMap((addAction: any) => {
            return this.liftService.create(addAction.payload).pipe(
                map((lift: any) => {
                    return new liftActions.AddLiftSuccessAction(this.mapLift(lift))
                }),
                catchError((error) => of(new liftActions.AddLiftFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    deleteLift$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DELETE_LIFT),
        switchMap((addAction: any) => {
            return this.liftService.delete(addAction.payload).pipe(
                map((lift: any) => {
                    this.router.navigate(['/lifts'], {relativeTo: this.route.parent});
                    return new liftActions.DeleteLiftSuccessAction(this.mapLift(lift))
                }),
                catchError((error) => of(new liftActions.DeleteLiftFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    editLiftName$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.RENAME_LIFT),
        switchMap((renameAction: any) => {
            return this.liftService.rename(renameAction.lift, renameAction.newName).pipe(
                map((lift: any) => {
                    return new liftActions.RenameLiftSuccessAction(this.mapLift(lift))
                }),
                catchError((error) => of(new liftActions.RenameLiftFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    addTrayToLift$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.ADD_TRAY),
        switchMap((addTrayAction: any) => {
            return this.liftService.addTray(addTrayAction.lift, addTrayAction.positions,
                addTrayAction.name, addTrayAction.trayType).pipe(
                map((lift: any) => {
                    return new liftActions.AddTraySuccessAction(this.mapTray(lift))
                }),
                catchError((error) => of(new liftActions.AddTrayFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    deleteTrayFromLift$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DELETE_TRAY),
        switchMap((deleteTrayAction: any) => {
            return this.liftService.deleteTray(deleteTrayAction.lift, deleteTrayAction.tray).pipe(
                map((lift: any) => {
                    return new liftActions.DeleteTraySuccessAction(this.mapTray(lift))
                }),
                catchError((error) => of(new liftActions.DeleteTrayFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    toggleTrayStateLift$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.TOGGLE_TRAY_STATE),
        switchMap((deleteTrayAction: any) => {
            return this.liftService.patchTray(deleteTrayAction.lift.id, deleteTrayAction.tray).pipe(
                map((lift: any) => {
                    return new liftActions.ToggleTrayStateSuccessAction(this.mapTray(lift))
                }),
                catchError((error) => of(new liftActions.ToggleTrayStateFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    updateTray$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.UPDATE_TRAY),
        switchMap((updateAction: any) => {
            return this.liftService.patchTray(updateAction.liftId, updateAction.tray).pipe(
                map((updatedTray: any) => {
                    return new liftActions.UpdateTraySuccessAction(this.mapTray(updatedTray))
                }),
                catchError((error) => of(new liftActions.UpdateTrayFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    loadTrayDetails$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_TRAY_DETAILS),
        switchMap((loadTrayDetails: LoadTrayDetailsAction) => {
            return this.liftService.loadTray(loadTrayDetails.liftId, loadTrayDetails.trayId, loadTrayDetails.filters).pipe(
                map((lift: any) => {
                    return new liftActions.LoadTrayDetailsSuccessAction(this.mapTray(lift))
                }),
                catchError((error) => of(new liftActions.LoadTrayDetailsFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    loadSlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_SLOT),
        switchMap((loadSlot: LoadSlotAction) => {
            return this.liftService.loadSlot(loadSlot.liftId, loadSlot.trayId, loadSlot.slotId, loadSlot.filters).pipe(
                map((lift: any) => {
                    return new liftActions.LoadSlotSuccessAction(this.mapSlot(lift))
                }),
                catchError((error) => of(new liftActions.LoadSlotFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    renameSlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.RENAME_SLOT),
        switchMap((loadSlot: RenameSlotAction) => {
            return this.liftService.renameSlot(loadSlot.liftId, loadSlot.trayId, loadSlot.slot).pipe(
                map((lift: any) => {
                    return new liftActions.RenameSlotSuccessAction(this.mapSlot(lift))
                }),
                catchError((error) => of(new liftActions.RenameSlotFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    addSlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.ADD_SLOT),
        switchMap((newSlot: AddSlotAction) => {
            return this.liftService.addSlot(newSlot.liftId, newSlot.trayId, newSlot.slot).pipe(
                map((lift: any) => {
                    return new liftActions.AddSlotSuccessAction(this.mapSlot(lift))
                }),
                catchError((error) => of(new liftActions.AddSlotFailedAction(error)))
            )
        })
    ));

    duplicateSlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DUPLICATE_SLOT),
        switchMap((newSlot: DuplicateSlotAction) => {
            return this.liftService.duplicateSlot(newSlot.liftId, newSlot.trayId, newSlot.id).pipe(
                map((lift: any) => {
                    return new liftActions.LoadDuplicatedSlotAction(newSlot.liftId, newSlot.trayId, lift.id)
                }),
                catchError((error) => of(new liftActions.DuplicateSlotFailedAction(error)))
            )
        })
    ));

    loadDuplicatedSlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_DUPLICATED_SLOT),
        switchMap((loadSlot: LoadDuplicatedSlotAction) => {
            return this.liftService.loadSlot(loadSlot.liftId, loadSlot.trayId, loadSlot.id).pipe(
                map((lift: any) => {
                    return new liftActions.LoadDuplicatedSlotSuccessAction(this.mapSlot(lift))
                }),
                catchError((error) => of(new liftActions.LoadSlotFailedAction(error)))
            )
        })
    ));

    copySlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.COPY_SLOT),
        switchMap((newSlot: CopySlotAction) => {
            return this.liftService.copySlot(newSlot.prevLiftId, newSlot.prevTrayId, newSlot.liftId, newSlot.trayId, newSlot.id).pipe(
                map((lift: any) => {
                    if (newSlot.prevTrayId === newSlot.trayId) {
                        return new liftActions.LoadCopiedSlotAction(newSlot.liftId, newSlot.trayId, lift.id);
                    }
                    return new liftActions.CopySlotSuccessAction();
                }),
                catchError((error) => of(new liftActions.CopySlotFailedAction(error)))
            )
        })
    ));

    loadCopiedSlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_COPIED_SLOT),
        switchMap((loadSlot: LoadCopiedSlotAction) => {
            return this.liftService.loadSlot(loadSlot.liftId, loadSlot.trayId, loadSlot.id).pipe(
                map((lift: any) => {
                    return new liftActions.LoadCopiedSlotSuccessAction(this.mapSlot(lift))
                }),
                catchError((error) => of(new liftActions.LoadSlotFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    deleteSlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DELETE_SLOT),
        switchMap((delSlot: DeleteSlotAction) => {
            return this.liftService.deleteSlot(delSlot.liftId, delSlot.trayId, delSlot.slotId).pipe(
                map((lift: any) => {
                    return new liftActions.DeleteSlotSuccessAction(this.mapSlot(lift))
                }),
                catchError((error) => of(new liftActions.DeleteSlotFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    loadTools$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_ALL_TOOLS),
        switchMap((loadTool: LoadAllToolsAction) => {
            return this.toolService.getTools(loadTool.config).pipe(
                map((tools: any) => {
                    return new liftActions.LoadAllToolsSuccessAction(this.mapTools(tools))
                }),
                catchError((error) => of(new liftActions.LoadAllToolsFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    loadToolsBySlot$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_ALL_TOOLS_BY_SLOT),
        switchMap((loadToolsForSlotAction: LoadAllToolsBySlotAction) => {
            return this.toolService.getToolsForSlot(loadToolsForSlotAction.slot.id).pipe(
                map((tools: any) => {
                    return new liftActions.LoadAllToolsSuccessAction(this.mapTools(tools))
                }),
                catchError((error) => of(new liftActions.LoadAllToolsFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    loadBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.LOAD_BATCH),
        switchMap((byBatch: LoadBatchAction) => {
            return this.batchService.loadBatch(byBatch.batchId, byBatch.config).pipe(
                map((tools: any) => {
                    return new liftActions.LoadBatchSuccessAction(this.mapBatch(tools))
                }),
                catchError((error) => of(new liftActions.LoadBatchFailedAction(error)))
            )
        })
    ));

    copyBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.COPY_BATCH),
        switchMap((byBatch: CopyBatchAction) => {
            return this.batchService.createBatch(byBatch.slotId, byBatch.name, byBatch.tools, byBatch.withStorage).pipe(
                map((batch: any) => {
                    return new liftActions.CopyBatchSuccessAction(this.mapBatch(batch))
                }),
                catchError((error) => of(new liftActions.CopyBatchFailedAction(error)))
            )
        })
    ));

    duplicateBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DUPLICATE_BATCH),
        switchMap((byBatch: DuplicateBatchAction) => {
            return this.batchService.duplicateBatch(byBatch.slotId, byBatch.batchId).pipe(
                map((batch: any) => {
                    return new liftActions.DuplicateBatchSuccessAction(this.mapBatch(batch));
                }),
                catchError((error) => of(new liftActions.DuplicateBatchFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    addToolsToBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.ADD_TOOLS_TO_BATCH),
        switchMap((byBatch: AddToolsToBatchAction) => {
            return this.batchService.addToolsToBatch(byBatch.batchId, byBatch.tools, byBatch.withStorage).pipe(
                map((tools: any) => {
                    return new liftActions.AddToolsToBatchSuccessAction(this.mapBatch(tools))
                }),
                catchError((error) => of(new liftActions.AddToolsToBatchFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    createBatchWithTools$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.CREATE_BATCH_WITH_TOOLS),
        switchMap((byBatch: CreateBatchWithToolsAction) => {
            return this.batchService.createBatch(byBatch.slotId, byBatch.name, byBatch.tools, byBatch.withStorage).pipe(
                map((tools: any) => {
                    return new liftActions.CreateBatchWithToolsSuccessAction(this.mapBatch(tools))
                }),
                catchError((error) => of(new liftActions.CreateBatchWithToolsFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    removeToolsFromBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.REMOVE_TOOLS_FROM_BATCH),
        switchMap((byBatch: RemoveToolsFromBatchAction) => {
            return this.batchService.removeToolsFromBatch(byBatch.batchId, byBatch.tools).pipe(
                map((tools: any) => {
                    return new liftActions.RemoveToolsFromBatchSuccessAction(this.mapBatch(tools))
                }),
                catchError((error) => of(new liftActions.RemoveToolsFromBatchFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    deleteBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.DELETE_BATCH),
        switchMap((byBatch: DeleteBatchAction) => {
            return this.batchService.deleteBatch(byBatch.batchId).pipe(
                map((tools: any) => {
                    return new liftActions.DeleteBatchSuccessAction(this.mapBatch(tools))
                }),
                catchError((error) => of(new liftActions.DeleteBatchFailedAction(error)))
            )
        })
    ));

    // tslint:disable-next-line
    updateCartBatchTools$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.UPDATE_CART_BATCH_TOOLS),
        switchMap((payload: UpdateCartBatchToolsAction) => {
            return this.batchService.updateCartBatchTools(payload.batchId, payload.tools, payload.cart).pipe(
                map((response: any) => {
                    return new liftActions.UpdateCartBatchToolsSuccessAction(this.mapBatch(response))
                }),
                catchError((error) => of(new liftActions.UpdateCartBatchToolsActionFailedAction(error)))
            )
        })
    ));

    updateBatch$ = createEffect(() => this.actions$.pipe(
        ofType(liftActions.UPDATE_BATCH),
        switchMap((payload: UpdateBatchAction) => {
            return this.batchService.updateBatch(payload.batch).pipe(
                map((updatedBatch: any) => {
                    return new liftActions.UpdateBatchSuccessAction(this.mapBatch(updatedBatch))
                }),
                catchError((error) => of(new liftActions.UpdateBatchFailedAction(error)))
            )
        })
    ));


    constructor(private actions$: Actions,
                private router: Router,
                private route: ActivatedRoute,
                private translateService: TranslateService,
                private toolService: ToolService,
                private plantService: PlantService,
                private lineService: LineService,
                private batchService: BatchService,
                private liftService: LiftsService) {
    }

    private mapPlants(plants: any) {
        return plants.items.map(plant => new Plant(plant));
    }

    private mapLifts(lifts: any) {
        return {
            items: lifts.items.map(lift => this.mapLift(lift)),
            total: lifts.total
        };
    }

    private mapLift(lift: any) {
        return new Lift(lift);
    }

    private mapTray(tray: Tray) {
        return new Tray(tray);
    }

    private mapLines(payload: any) {
        return payload.items.map(line => new Line(line));
    }

    private mapSlot(slot: Slot) {
        return new Slot(slot);
    }

    private mapBatch(batch: any) {
        return new Batch({
            id: batch.id,
            name: batch.name,
            tools: this.mapGroups(batch.tools)
        });
    }

    private mapTools(tools: any) {
        return tools;
    }

    private mapGroups = res => {
        const body = res;
        if (!body || !body.items) {
            return null;
        }

        return {
            tools: this.mapGroupableItems(body.items),
            count: body.total,
            totalCount: body.totalTools
        };
    };

    mapGroupableItems(items) {
        const groups = items.map(group => {
            if (group.subGroup) {
                return new Group(group);
            }
            return new GroupChildWrapper(group).item;
        });
        return groups;
    }

    private mapSets = response => {
        return {
            items: response,
            total: response.length
        }
    };
}
